import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import constant from '../../constant';
import HospitalForm from '../ui/hospitalForm';

const EditHospital = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHospital = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();

        if (response.ok) {
          const data = result;
          setInitialValues({
            hospital_id: data.hospital_id,
            name: data.name,
            is_active: data.is_active,
            created_on: data.created_on,
            street: data.street,
            city: data.city,
            state: data.state,
            pincode: data.pincode,
            country: data.country,
            logo: data.logo,
          });
        } else {
          setError(result.message || 'Failed to fetch hospital data');
        }
      } catch (error) {
        setError('Error fetching hospital data');
      } finally {
        setLoading(false);
      }
    };

    fetchHospital();
  }, [id]);

  const handleCancel = () => {
    navigate('/hospitals');
  };

  const handleSubmit = async (hospitalData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const apiData = {
        name: hospitalData.name,
        street: hospitalData.street,
        city: hospitalData.city,
        state: hospitalData.state,
        pincode: hospitalData.pincode,
        logo: hospitalData.logo,
        is_active: hospitalData.is_active,
      };

      const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const result = await response.json();

      if (response.ok) {
        navigate('/admin/hospitals');
      } else {
        setError(result.message || 'Failed to update hospital');
      }
    } catch (error) {
      setError('Error updating hospital');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Edit Hospital</h2>
          </div>
        </div>
        {loading && (
          <div className="bg-blue-50 text-blue-700 p-4 rounded-lg mb-6">
            Processing...
          </div>
        )}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        <HospitalForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isEditMode={true}
        />
      </div>
    </div>
  );
};

export default EditHospital;